import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url
).toString();

export default function PdfCanvasViewer({
  pdfUrl,
  currentPage = 1,
  onPageCount,
  children,
  className = '',
  surfaceProps = {},
  scrollable = true,
}) {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const canvasRef = useRef(null);
  const pdfRef = useRef(null);
  const renderTaskRef = useRef(null);
  const [pageSize, setPageSize] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [renderTick, setRenderTick] = useState(0);

  useEffect(() => {
    if (!pdfUrl) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPageSize(null);

    const task = pdfjsLib.getDocument(pdfUrl);
    task.promise
      .then((pdf) => {
        if (cancelled) {
          pdf.destroy();
          return;
        }
        pdfRef.current = pdf;
        onPageCount?.(pdf.numPages);
        setRenderTick((tick) => tick + 1);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[pdf] failed to load document', err);
          setError('Could not load PDF preview.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      task.destroy();
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
      if (pdfRef.current) {
        pdfRef.current.destroy();
        pdfRef.current = null;
      }
    };
  }, [pdfUrl, onPageCount]);

  useEffect(() => {
    const onResize = () => setRenderTick((tick) => tick + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const pdf = pdfRef.current;
    const canvas = canvasRef.current;
    const container = contentRef.current || containerRef.current;
    if (!pdf || !canvas || !container) return undefined;

    let cancelled = false;
    setError(null);

    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }

    pdf.getPage(currentPage)
      .then((page) => {
        if (cancelled) return;
        const baseViewport = page.getViewport({ scale: 1 });
        const availableWidth = Math.max(container.clientWidth || baseViewport.width, 320);
        const cssWidth = Math.min(availableWidth, baseViewport.width * 1.6);
        const scale = cssWidth / baseViewport.width;
        const deviceScale = window.devicePixelRatio || 1;
        const viewport = page.getViewport({ scale: scale * deviceScale });
        const cssHeight = baseViewport.height * scale;

        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${cssWidth}px`;
        canvas.style.height = `${cssHeight}px`;
        setPageSize({ width: cssWidth, height: cssHeight });

        const context = canvas.getContext('2d');
        const renderTask = page.render({ canvasContext: context, viewport });
        renderTaskRef.current = renderTask;
        return renderTask.promise;
      })
      .catch((err) => {
        if (!cancelled && err?.name !== 'RenderingCancelledException') {
          console.error('[pdf] failed to render page', err);
          setError('Could not render this PDF page.');
        }
      });

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [currentPage, renderTick]);

  return (
    <div ref={containerRef} className={`w-full ${scrollable ? 'overflow-auto' : 'overflow-visible'} bg-zinc-100 ${className}`}>
      <div className="min-h-[320px] w-full p-3 sm:p-4">
        <div ref={contentRef} className="w-full grid justify-items-center">
          {(!pdfUrl || (loading && !pageSize)) && (
            <div className="min-h-[320px] grid place-items-center text-sm text-zinc-500">
              Loading PDF preview...
            </div>
          )}
          {error && (
            <div className="min-h-[320px] grid place-items-center text-sm text-red-700">
              {error}
            </div>
          )}
          <div
            {...surfaceProps}
            className={`relative bg-white shadow-sm ${surfaceProps.className || ''}`}
            style={{
              width: pageSize ? `${pageSize.width}px` : undefined,
              height: pageSize ? `${pageSize.height}px` : undefined,
              display: error ? 'none' : undefined,
              ...surfaceProps.style,
            }}
          >
            <canvas ref={canvasRef} className="block bg-white" />
            {pageSize && (
              <div className="absolute inset-0">
                {children}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
